import { useId, type FormEvent } from "react";
import {
  ACCOUNT_KINDS,
  type AccountKind,
  type MoneyAccount,
  type MoneyAccountInput,
} from "@/api/cash";
import type { BankAccount } from "@/api/settings";
import { RequestError } from "@/components/RequestError";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type MoneyAccountFormProps = {
  initial?: MoneyAccount | null;
  bankAccounts: BankAccount[];
  onSubmit: (input: MoneyAccountInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

export function MoneyAccountForm({
  initial,
  bankAccounts,
  onSubmit,
  onCancel,
  onReload,
}: MoneyAccountFormProps) {
  const id = useId();
  const { values, set } = useFormValues({
    name: initial?.name ?? "",
    kind: (initial?.kind ?? "cash") as AccountKind,
    currency: initial?.currency ?? "INR",
    bank_account_id: initial?.bank_account_id ?? "",
    opening_balance: initial?.opening_balance ?? "0.00",
    opening_on: initial?.opening_on ?? "",
    is_default_in: initial?.is_default_in ?? false,
    is_default_out: initial?.is_default_out ?? false,
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        name: values.name.trim(),
        kind: values.kind,
        currency: values.currency.trim().toUpperCase(),
        bank_account_id: values.kind === "bank" ? values.bank_account_id || null : null,
        opening_balance: values.opening_balance.trim() || "0.00",
        opening_on: values.opening_on || null,
        is_default_in: values.is_default_in,
        is_default_out: values.is_default_out,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-name`}
          label="Name"
          value={values.name}
          onChange={(v) => set("name", v)}
          error={errors.name}
        />
        <SelectField
          id={`${id}-kind`}
          label="Kind"
          value={values.kind}
          onChange={(v) => set("kind", v as AccountKind)}
          options={ACCOUNT_KINDS}
          error={errors.kind}
        />
        <TextField
          id={`${id}-currency`}
          label="Currency"
          value={values.currency}
          onChange={(v) => set("currency", v)}
          error={errors.currency}
          hint="Three letters; every figure of this account is in it"
        />
        {values.kind === "bank" && (
          <SelectField
            id={`${id}-bank`}
            label="Company bank account"
            value={values.bank_account_id}
            onChange={(v) => set("bank_account_id", v)}
            placeholder="Not linked"
            options={bankAccounts.map((b) => ({
              value: b.id,
              label: `${b.label} (${b.currency})`,
            }))}
            error={errors.bank_account_id}
          />
        )}
        <TextField
          id={`${id}-opening`}
          label="Opening balance"
          value={values.opening_balance}
          onChange={(v) => set("opening_balance", v)}
          error={errors.opening_balance}
          hint="What it held before the app tracked it; negative for a person the company owed"
        />
        <TextField
          id={`${id}-opening-on`}
          label="Opening date"
          type="date"
          value={values.opening_on}
          onChange={(v) => set("opening_on", v)}
          error={errors.opening_on}
          hint="Payments and expenses before this day are inside the opening balance"
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
        <Checkbox
          id={`${id}-in`}
          label="Receipts land here by default"
          checked={values.is_default_in}
          onChange={(v) => set("is_default_in", v)}
        />
        <Checkbox
          id={`${id}-out`}
          label="Payments leave from here by default"
          checked={values.is_default_out}
          onChange={(v) => set("is_default_out", v)}
        />
      </div>
      <FormActions
        saving={saving}
        submitLabel={initial ? "Save account" : "Add account"}
        onCancel={onCancel}
      />
    </form>
  );
}
